import { useEffect, useMemo, useState } from 'react';
import { Avatar, Dropdown, Layout, Menu, Space, Tabs, Typography, theme } from 'antd';
import type { MenuProps, TabsProps } from 'antd';
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getNextTabKeyAfterClose,
  navigationActions,
  selectActiveTab,
  selectTabs,
} from '@/store/slices/navigationSlice';
import { authActions, selectCurrentUser } from '@/store/authSlice';
import {
  buildMenuItems,
  findRouteMeta,
  getOpenKeysForPath,
  getSelectedMenuKey,
  appRoutes,
} from '@/router/routes';
import { logger } from '@/utils/logger';
import logoImg from '@/assets/logo.png';
import './AppShell.css';

const { Header, Sider, Content, Footer } = Layout;
const { Text } = Typography;

const AppShell = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const tabs = useAppSelector(selectTabs);
  const activeKey = useAppSelector(selectActiveTab);
  const currentUser = useAppSelector(selectCurrentUser);
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const menuItems = useMemo(() => buildMenuItems(appRoutes), []);
  const selectedMenuKey = getSelectedMenuKey(location.pathname);
  const mergedOpenKeys = useMemo(
    () => Array.from(new Set([...openKeys, ...getOpenKeysForPath(location.pathname)])),
    [location.pathname, openKeys],
  );

  useEffect(() => {
    const matchedRoute = findRouteMeta(location.pathname);

    if (matchedRoute) {
      dispatch(
        navigationActions.syncRoute({
          key: location.pathname,
          label: matchedRoute.label,
          closable: matchedRoute.closable,
        }),
      );
      return;
    }

    logger.warn('Unregistered route visited', { pathname: location.pathname });
  }, [dispatch, location.pathname]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (typeof key === 'string' && key.startsWith('/')) {
      navigate(key);
    }
  };

  const handleTabChange = (key: string) => {
    dispatch(navigationActions.setActiveTab(key));
    navigate(key);
  };

  const handleTabEdit: TabsProps['onEdit'] = (targetKey, action) => {
    if (action !== 'remove' || typeof targetKey !== 'string') {
      return;
    }

    const nextKey = getNextTabKeyAfterClose(tabs, targetKey, activeKey);
    dispatch(navigationActions.removeTab(targetKey));
    dispatch(navigationActions.setActiveTab(nextKey));
    navigate(nextKey);
  };

  const userMenu: MenuProps = {
    items: [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: '个人中心',
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
      },
    ],
    onClick: ({ key }) => {
      if (key === 'profile') {
        navigate('/home/profile');
      }

      if (key === 'logout') {
        dispatch(authActions.logout());
        dispatch(navigationActions.resetTabs());
        navigate('/login', { replace: true });
      }
    },
  };

  return (
    <Layout className="home-layout">
      <Sider
        breakpoint="lg"
        className="home-sider"
        collapsed={collapsed}
        collapsedWidth={60}
        collapsible
        onBreakpoint={setCollapsed}
        trigger={null}
        width={220}
      >
        <div className="sider-logo-container">
          <img alt="Logo" className="sider-logo" src={logoImg} />
          {!collapsed && <span className="sider-title">置业礼包</span>}
        </div>

        <Menu
          className="sider-menu"
          items={menuItems}
          mode="inline"
          onClick={handleMenuClick}
          onOpenChange={(keys) => setOpenKeys(keys as string[])}
          openKeys={mergedOpenKeys}
          selectedKeys={selectedMenuKey ? [selectedMenuKey] : []}
          theme="dark"
        />

        <div className="sider-collapse-btn" onClick={() => setCollapsed((value) => !value)}>
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      </Sider>

      <Layout className="site-layout">
        <Header className="site-header" style={{ background: colorBgContainer }}>
          <div className="header-right">
            <Dropdown arrow menu={userMenu} placement="bottomRight">
              <Space className="user-dropdown-link">
                <Avatar
                  icon={<UserOutlined />}
                  src={logoImg}
                  style={{ backgroundColor: '#1677ff' }}
                />
                <Text>{currentUser?.name ?? '管理员'}</Text>
              </Space>
            </Dropdown>
          </div>
        </Header>

        <div className="site-tabs">
          <Tabs
            activeKey={activeKey}
            hideAdd
            items={tabs.map((tab) => ({
              key: tab.key,
              label: tab.label,
              closable: tab.closable,
            }))}
            onChange={handleTabChange}
            onEdit={handleTabEdit}
            type="editable-card"
          />
        </div>

        <Content className="site-content">
          <div className="content-card">
            <Outlet />
          </div>
        </Content>

        <Footer style={{ color: '#8c8c8c', textAlign: 'center' }}>
          Copyright © 2026 置业礼包
        </Footer>
      </Layout>
    </Layout>
  );
};

export default AppShell;
